import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import LocalBarOutlinedIcon from "@mui/icons-material/LocalBarOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import ServiceImagePreview, { FoodServicePlaceholder, BarServicePlaceholder } from "./ServiceImagePreview";
import MenuGalleryModal, { MenuPreviewCard, MenuPlaceholder } from "./MenuGalleryModal";
import ExploreGalleryModal from "./ExploreGalleryModal";
import { getFoodServiceThumb, getMenuThumb, getFoodServiceData, getFoodServiceGallery } from "./foodServiceData";
import { getBarServiceThumb, getBarServiceData, getBarServiceGallery } from "./barServiceData";
import {
  useGetBanquetServicesMutation,
  useGetBanquetPricingMutation,
} from "../../../Api/attoDeskApi";
import {
  BookingFormState,
  LED_PRICING,
  computeLEDTotal,
  PaymentEntry,
  PAYMENT_METHODS,
  SECURITY_METHODS,
  computeFinancials,
  fmtCAD,
  n,
} from "./bookingTypes";

/* ═══════════════════════════════════════════════════════════════════════════
   SERVICE MEDIA HELPERS — prefer DB images, fall back to hardcoded
   ═══════════════════════════════════════════════════════════════════════════ */
interface ServiceMediaResult {
  coverImage: string;
  description: string;
  badge?: string;
  mediaType?: "image" | "video";
  video?: string;
  gallery: { image: string; title: string; tag?: string; mediaType?: "image" | "video"; video?: string }[];
}

function resolveServiceMedia(
  service: { name: string; image?: string | null; images?: { id: number; image_url: string; title?: string | null; tag?: string | null; is_cover: boolean; sort_order: number }[] } | undefined,
  type: "food" | "bar" | "menu",
): ServiceMediaResult {
  const name = service?.name || "";
  const dbImages = service?.images ?? [];
  const cover = dbImages.find(i => i.is_cover) || dbImages[0];

  // If DB has images, use them as primary source
  if (dbImages.length > 0) {
    return {
      coverImage: cover?.image_url || service?.image || "",
      description: `${name} — premium ${type} service for your event`,
      gallery: dbImages
        .filter(i => !i.is_cover || dbImages.length === 1)
        .map(i => ({ image: i.image_url, title: i.title || name, tag: i.tag || undefined })),
    };
  }

  // Fall back to hardcoded data
  if (type === "food") {
    const data = getFoodServiceData(name);
    const gallery = getFoodServiceGallery(name);
    return {
      coverImage: service?.image || data?.image || "",
      description: data?.description || "",
      badge: data?.badge,
      gallery: gallery.map(g => ({ image: g.image, title: g.title, tag: g.tag, mediaType: g.mediaType, video: g.video })),
    };
  }
  if (type === "bar") {
    const data = getBarServiceData(name);
    const gallery = getBarServiceGallery(name);
    return {
      coverImage: service?.image || data?.image || "",
      description: data?.description || "",
      badge: data?.badge,
      mediaType: (data as any)?.mediaType,
      video: (data as any)?.video,
      gallery: gallery.map(g => ({ image: g.image, title: g.title, tag: g.tag, mediaType: g.mediaType, video: g.video })),
    };
  }
  return { coverImage: service?.image || "", description: "", gallery: [] };
}

/* ═══════════════════════════════════════════════════════════════════════════
   TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy:    "#0f172a",
  gold:    "#d4a373",
  cream:   "#f8f5f0",
  white:   "#ffffff",
  bg:      "#f8f5f0",
  slate:   "#6b7280",
  slateLt: "#9ca3af",
  line:    "#e5e7eb",
  doneBg:  "#f8f5f0",
  doneTx:  "#6b7280",
  doneBd:  "#d4a373",
  redTx:   "#9b2c2c",
  peach:   "#f8f5f0",
  inputBg: "#fafaf9",
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION CARD
   ═══════════════════════════════════════════════════════════════════════════ */
const SectionCard = React.forwardRef<HTMLDivElement, {
  icon: React.ReactNode; title: string; subtitle?: string;
  action?: React.ReactNode; children: React.ReactNode;
  completed?: boolean; active?: boolean; badge?: string;
}>(({ icon, title, subtitle, action, children, completed, active, badge }, ref) => (
  <Box ref={ref} sx={{
    bgcolor: T.white, borderRadius: "16px",
    boxShadow: active ? `0 0 0 2px ${T.gold}30, 0 4px 20px rgba(0,0,0,0.04)` : "0 1px 3px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.02)",
    transition: "all 0.3s ease",
    opacity: completed && !active ? 0.85 : 1,
    "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.03)" },
  }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: { xs: 2.5, md: 3 }, pt: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          bgcolor: completed ? T.doneBg : active ? `${T.gold}15` : T.bg,
          color: completed ? T.doneTx : T.gold,
          transition: "all 0.2s",
        }}>
          {completed ? <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.doneTx }}>&#10003;</Typography> : icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy, lineHeight: 1.2 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.2 }}>{subtitle}</Typography>}
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {badge && <Chip label={badge} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: T.peach, color: T.navy }} />}
        {action}
      </Box>
    </Box>
    <Box sx={{ px: { xs: 2.5, md: 3 }, pt: 2, pb: 3 }}>{children}</Box>
  </Box>
));

/* ═══════════════════════════════════════════════════════════════════════════
   INPUT STYLES
   ═══════════════════════════════════════════════════════════════════════════ */
const inputSx = {
  "& .MuiInputBase-root": {
    fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg,
    border: `1px solid ${T.line}`,
    "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold },
    transition: "all 0.15s ease",
    "& fieldset": { border: "none" },
  },
  "& .MuiInputLabel-root": { fontSize: 13 },
};

/* ═══════════════════════════════════════════════════════════════════════════
   DAY TYPE HELPER
   ═══════════════════════════════════════════════════════════════════════════ */
function getDayType(dateStr: string): string {
  if (!dateStr) return "Mon-Thu";
  const d = new Date(dateStr + "T12:00:00");
  const dow = d.getDay(); // 0=Sun
  if (dow === 0) return "Sun";
  if (dow === 6) return "Sat";
  if (dow === 5) return "Fri";
  return "Mon-Thu";
}

/* ═══════════════════════════════════════════════════════════════════════════
   PROPS & SERVICE SHAPE
   ═══════════════════════════════════════════════════════════════════════════ */
interface Props {
  form: BookingFormState;
  setForm: React.Dispatch<React.SetStateAction<BookingFormState>>;
}

interface ServiceImageRecord {
  id: number; service_id: number; image_url: string;
  title?: string | null; tag?: string | null;
  is_cover: boolean; sort_order: number;
}

interface ServiceItem {
  id: number; serviceType: string; category: string | null;
  name: string; price: number; pricingType: "pp" | "flat" | "included";
  durationHours: number | null; image?: string | null;
  images?: ServiceImageRecord[];
}

interface PricingRecord {
  hallKey: string; dayType: string; timeSlot: string;
  flatFee: number; pricePerPerson: number;
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const Step2Services = ({ form, setForm }: Props) => {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [allPricing, setAllPricing] = useState<PricingRecord[]>([]);
  const [isOverridden, setIsOverridden] = useState(false);
  const lastAutoHall = useRef<string>("");

  // Section refs for auto-scroll
  const hallRef = useRef<HTMLDivElement>(null);
  const ledRef = useRef<HTMLDivElement>(null);
  const foodRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const finRef = useRef<HTMLDivElement>(null);

  // Scroll helper
  const scrollTo = (ref: React.RefObject<HTMLDivElement>) => {
    setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  };

  // Section completion tracking
  const hallDone = n(form.hallFlatFee) > 0 || n(form.hallPricePerPerson) > 0;
  const ledDone = form.selectedLEDs.length > 0;
  const foodDone = !!form.foodServiceOption || !!form.menuOption;
  const barDone = !!form.barServiceOption || !!form.barPackageOption;

  const [getServices] = useGetBanquetServicesMutation();
  const [getPricing] = useGetBanquetPricingMutation();

  // ── Fetch services ──
  useEffect(() => {
    (async () => {
      try {
        const res = (await getServices({}).unwrap()) as any;
        if (res.status && Array.isArray(res.data)) setServices(res.data);
      } catch {}
    })();
  }, [getServices]);

  // ── Fetch all pricing data ──
  useEffect(() => {
    (async () => {
      try {
        const res = (await (getPricing as any)({}).unwrap()) as any;
        if (res.status && Array.isArray(res.data)) setAllPricing(res.data);
      } catch {}
    })();
  }, [getPricing]);

  // ── Auto-fill hall pricing when hall/date/slot changes ──
  const selectedHallKey = form.halls[0]?.hallKey ?? "";
  const dayType = getDayType(form.eventDate);
  const timeSlot = form.timeSlot || "PM";

  useEffect(() => {
    if (!selectedHallKey || allPricing.length === 0) return;

    // If hall changed, reset override
    if (lastAutoHall.current !== selectedHallKey) {
      setIsOverridden(false);
      lastAutoHall.current = selectedHallKey;
    }

    if (isOverridden) return;

    // Find matching pricing record
    const match = allPricing.find(
      (p) => p.hallKey === selectedHallKey && p.dayType === dayType && p.timeSlot === timeSlot,
    );

    if (match) {
      setForm((prev) => ({
        ...prev,
        hallFlatFee: String(match.flatFee ?? 0),
        hallPricePerPerson: String(match.pricePerPerson ?? 0),
      }));
    }
  }, [selectedHallKey, dayType, timeSlot, allPricing, isOverridden, setForm]);

  // ── Helpers ──
  const byType = (type: string) => services.filter((s) => s.serviceType === type);
  const priceLabel = (s: ServiceItem) =>
    s.pricingType === "pp" ? `$${s.price}/person` : s.pricingType === "flat" ? `$${s.price} flat` : "Included";

  const set = (field: keyof BookingFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const setVal = (field: keyof BookingFormState, value: string | number) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleHallFieldChange = (field: "hallFlatFee" | "hallPricePerPerson") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsOverridden(true);
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const resetOverride = () => {
    setIsOverridden(false);
    lastAutoHall.current = ""; // force re-lookup
    // Trigger by changing ref so the useEffect re-runs
    setTimeout(() => { lastAutoHall.current = selectedHallKey; }, 0);
    const match = allPricing.find(
      (p) => p.hallKey === selectedHallKey && p.dayType === dayType && p.timeSlot === timeSlot,
    );
    if (match) {
      setForm((prev) => ({
        ...prev,
        hallFlatFee: String(match.flatFee ?? 0),
        hallPricePerPerson: String(match.pricePerPerson ?? 0),
      }));
    }
  };

  const handleServiceSelect = (optionField: keyof BookingFormState, priceField: keyof BookingFormState, serviceType: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sel = services.find((s) => s.serviceType === serviceType && s.name === e.target.value);
      setForm((prev) => ({ ...prev, [optionField]: e.target.value, [priceField]: sel ? String(sel.price) : "0" }));
    };

  const handleBarPackageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sel = services.find((s) => s.serviceType === "package" && s.name === e.target.value);
    setForm((prev) => ({ ...prev, barPackageOption: e.target.value, barPackagePricePerPerson: sel ? String(sel.price) : "0", barHours: sel?.durationHours ? String(sel.durationHours) : prev.barHours }));
  };

  // ── Gallery modals ──
  const [menuGalleryOpen, setMenuGalleryOpen] = useState(false);
  const [foodGalleryOpen, setFoodGalleryOpen] = useState(false);
  const [barGalleryOpen, setBarGalleryOpen] = useState(false);

  // ── Adjacent-hall toast ──
  const [adjToast, setAdjToast] = useState(false);

  // ── LED alert toast ──
  const [ledToast, setLedToast] = useState("");

  // ── Hall-change detection for LED reset ──
  const prevHallKey = useRef(form.halls.map(h => h.hallKey).join(","));
  const [hallChangeAlert, setHallChangeAlert] = useState(false);

  useEffect(() => {
    const currentKey = form.halls.map(h => h.hallKey).join(",");
    if (prevHallKey.current && prevHallKey.current !== currentKey && currentKey !== "") {
      // Halls changed — reset LED selections, keep only auto-included ones
      setForm(p => ({ ...p, selectedLEDs: [] }));
      setHallChangeAlert(true);
      setTimeout(() => setHallChangeAlert(false), 4000);
    }
    prevHallKey.current = currentKey;
  }, [form.halls, setForm]);

  // ── Payments ──
  const addPayment = () => setForm((prev) => ({ ...prev, payments: [...prev.payments, { amount: "", method: "Cash", paymentDate: "" }] }));
  const updatePayment = (idx: number, field: keyof PaymentEntry, value: string) => setForm((prev) => ({ ...prev, payments: prev.payments.map((p, i) => i === idx ? { ...p, [field]: value } : p) }));
  const removePayment = (idx: number) => setForm((prev) => ({ ...prev, payments: prev.payments.filter((_, i) => i !== idx) }));

  // ── Financials ──
  const fin = computeFinancials(form);
  const guests = n(form.guestCount);
  const flatFee = n(form.hallFlatFee);
  const perPerson = n(form.hallPricePerPerson);
  const totalDeposits = form.payments.reduce((s, p) => s + n(p.amount), 0);
  const balance = fin.grandTotal - totalDeposits;

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 3.5 } }}>

      {/* ══════════ GUESTS & HALL PRICING ══════════ */}
      <SectionCard
        ref={hallRef}
        icon={<AttachMoneyIcon sx={{ fontSize: 17 }} />}
        title="Guests & Hall Pricing"
        completed={hallDone}
        active={!hallDone}
        badge={hallDone ? "Done" : undefined}
        subtitle={selectedHallKey ? `Pricing for ${selectedHallKey} · ${dayType} · ${timeSlot}` : "Select a hall in Step 1"}
        action={
          selectedHallKey ? (
            <Tooltip title={isOverridden ? "Custom pricing — click to reset to auto" : "Auto-filled from pricing table"}>
              <Chip
                size="small"
                icon={isOverridden ? <LockOpenOutlinedIcon sx={{ fontSize: 13 }} /> : <AutoAwesomeOutlinedIcon sx={{ fontSize: 13 }} />}
                label={isOverridden ? "Custom" : "Auto"}
                onClick={isOverridden ? resetOverride : undefined}
                sx={{
                  height: 24, fontSize: 10, fontWeight: 600, borderRadius: "8px", cursor: isOverridden ? "pointer" : "default",
                  bgcolor: isOverridden ? T.peach : T.doneBg,
                  color: isOverridden ? T.navy : T.doneTx,
                  border: `1px solid ${isOverridden ? T.gold : T.doneBd}`,
                }}
              />
            </Tooltip>
          ) : null
        }
      >
        <Grid container spacing={2.5}>
          <Grid item xs={6} sm={4}>
            <TextField label="Guest Count *" type="number" size="small" fullWidth
              value={form.guestCount} onChange={set("guestCount")}
              error={n(form.guestCount) < 1} inputProps={{ min: 1 }} sx={inputSx} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Children" type="number" size="small" fullWidth
              value={form.childrenCount} onChange={set("childrenCount")}
              inputProps={{ min: 0 }} sx={inputSx} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField label="Guest Minimum" type="number" size="small" fullWidth
              value={form.guestMinimum} onChange={set("guestMinimum")}
              inputProps={{ min: 0 }} sx={inputSx} />
          </Grid>
        </Grid>

        {/* Divider */}
        <Box sx={{ height: 1, bgcolor: T.line, my: 3 }} />

        {/* Hall pricing fields */}
        <Grid container spacing={2.5} alignItems="flex-end">
          <Grid item xs={6} sm={3}>
            <TextField label="Hall Flat Fee ($)" type="number" size="small" fullWidth
              value={form.hallFlatFee} onChange={handleHallFieldChange("hallFlatFee")}
              inputProps={{ min: 0, step: "any" }} sx={inputSx} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Per Person ($)" type="number" size="small" fullWidth
              value={form.hallPricePerPerson} onChange={handleHallFieldChange("hallPricePerPerson")}
              inputProps={{ min: 0, step: "any" }} sx={inputSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* Hall subtotal — premium card */}
            <Box sx={{
              position: "relative", overflow: "hidden",
              background: `linear-gradient(135deg, ${T.peach}90, ${T.peach}40)`,
              borderRadius: "16px", px: 2.5, py: 2,
              boxShadow: "0 2px 8px rgba(201,169,110,0.08), 0 6px 20px rgba(201,169,110,0.04)",
              borderLeft: `4px solid ${T.gold}`,
            }}>
              {/* Decorative circle */}
              <Box sx={{
                position: "absolute", top: -20, right: -20,
                width: 80, height: 80, borderRadius: "50%",
                bgcolor: "rgba(201,169,110,0.08)",
              }} />

              {/* Label */}
              <Typography sx={{
                fontSize: 9, fontWeight: 700, color: T.slateLt,
                textTransform: "uppercase", letterSpacing: "0.12em", mb: 1,
              }}>
                Hall Subtotal
              </Typography>

              {/* Main price */}
              <Typography sx={{
                fontSize: 28, fontWeight: 800, color: T.navy,
                lineHeight: 1, letterSpacing: "-0.02em",
                transition: "transform 0.2s ease",
              }}>
                {fmtCAD(fin.hallSubtotal)}
              </Typography>

              {/* Breakdown */}
              {(flatFee > 0 || perPerson > 0) && guests > 0 && (
                <Box sx={{
                  mt: 1.5, pt: 1.5,
                  borderTop: "1px solid rgba(201,169,110,0.2)",
                  display: "flex", gap: 2, flexWrap: "wrap",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: 9, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em" }}>Base</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>{fmtCAD(flatFee)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-end", pb: "2px" }}>
                    <Typography sx={{ fontSize: 14, color: T.slateLt, fontWeight: 300 }}>+</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 9, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em" }}>Per Guest</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.navy }}>
                      {guests} <Typography component="span" sx={{ fontSize: 11, color: T.slateLt, fontWeight: 400 }}>×</Typography> {fmtCAD(perPerson)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </SectionCard>

      {/* ══════════ LED SCREENS SETUP ══════════ */}
      {(() => {
        const noHalls = form.halls.length === 0;
        const hallKeys = form.halls.map(h => h.hallKey);
        const HALL_ORDER = ["Nova", "Titan", "Lunar", "Eclipse"] as const;
        const HALL_CAP: Record<string, number> = { Nova: 250, Titan: 250, Lunar: 250, Eclipse: 250 };

        // Extract individual halls from current combo key(s)
        const selectedIndividual = hallKeys.flatMap(k =>
          k === "Full Hall" ? [...HALL_ORDER] : k.split(" + ").filter(h => (HALL_ORDER as readonly string[]).includes(h))
        );

        const isContiguous = (halls: string[]) => {
          if (halls.length <= 1) return true;
          const idxs = halls.map(h => HALL_ORDER.indexOf(h as any)).sort((a, b) => a - b);
          for (let i = 1; i < idxs.length; i++) {
            if (idxs[i] !== idxs[i - 1] + 1) return false;
          }
          return true;
        };

        const getAllowedHalls = (): string[] => {
          if (selectedIndividual.length === 0) return [...HALL_ORDER];
          const idxs = selectedIndividual.map(h => HALL_ORDER.indexOf(h as any));
          const min = Math.min(...idxs);
          const max = Math.max(...idxs);
          return HALL_ORDER.filter((hall, i) =>
            selectedIndividual.includes(hall) || i === min - 1 || i === max + 1
          );
        };

        const canClickHall = (hall: string) => {
          if (selectedIndividual.includes(hall)) {
            return isContiguous(selectedIndividual.filter(h => h !== hall));
          }
          return getAllowedHalls().includes(hall);
        };

        const handleHallClick = (hall: string) => {
          if (!canClickHall(hall)) {
            setAdjToast(true);
            return;
          }

          let updated: string[];
          if (selectedIndividual.includes(hall)) {
            updated = selectedIndividual.filter(h => h !== hall);
          } else {
            updated = [...selectedIndividual, hall];
          }

          if (updated.length === 0) {
            setForm(p => ({ ...p, halls: [] }));
            return;
          }

          const sorted = [...updated].sort((a, b) => HALL_ORDER.indexOf(a as any) - HALL_ORDER.indexOf(b as any));
          const key = sorted.length === 4 ? "Full Hall" : sorted.join(" + ");
          const cap = sorted.length === 4 ? 900 : sorted.reduce((s, h) => s + (HALL_CAP[h] || 250), 0);
          setForm(p => ({ ...p, halls: [{ hallKey: key, hallLabel: key, hallCapacity: cap }] }));
        };

        const hasNova = selectedIndividual.includes("Nova");
        const hasEclipse = selectedIndividual.includes("Eclipse");
        const hasTitan = selectedIndividual.includes("Titan");
        const hasLunar = selectedIndividual.includes("Lunar");

        const leds = [
          { name: "LED 1", desc: "Main stage left", price: LED_PRICING["LED 1"] ?? 3000 },
          { name: "LED 2", desc: "Main stage right", price: LED_PRICING["LED 2"] ?? 3000 },
          { name: "LED 3", desc: "Side panel", price: LED_PRICING["LED 3"] ?? 1800 },
          { name: "LED 4", desc: "Rear projection", price: LED_PRICING["LED 4"] ?? 1800 },
        ];

        // LED 1 available only with Nova, LED 2 only with Eclipse, LED 3 & 4 always available
        const getState = (name: string): "available" | "disabled" => {
          if (name === "LED 1") return hasNova ? "available" : "disabled";
          if (name === "LED 2") return hasEclipse ? "available" : "disabled";
          if (name === "LED 3" || name === "LED 4") return selectedIndividual.length > 0 ? "available" : "disabled";
          return "disabled";
        };

        const getReason = (name: string) => {
          if (name === "LED 1") return hasNova ? "Available with Nova Hall" : "Available only with Nova Hall";
          if (name === "LED 2") return hasEclipse ? "Available with Eclipse Hall" : "Available only with Eclipse Hall";
          return selectedIndividual.length > 0 ? "Available for any hall" : "Select a hall first";
        };

        const toggleLed = (name: string) => {
          if (noHalls) { setLedToast("Please select halls first"); return; }
          const st = getState(name);
          if (st === "disabled") {
            if (name === "LED 1") setLedToast("LED 1 is only available with Nova Hall");
            else if (name === "LED 2") setLedToast("LED 2 is only available with Eclipse Hall");
            else setLedToast("This LED is not available for your selected halls");
            return;
          }
          setForm(p => ({
            ...p,
            selectedLEDs: p.selectedLEDs.includes(name) ? p.selectedLEDs.filter(l => l !== name) : [...p.selectedLEDs, name],
          }));
        };

        // Only user-selected LEDs count (no auto-include)
        const allActive = form.selectedLEDs.filter(l => getState(l) !== "disabled");
        const ledCost = computeLEDTotal(allActive, form.halls);
        const bothMainLeds = allActive.includes("LED 1") && allActive.includes("LED 2");

        // Recommend
        const recLeds: string[] = [];
        if (hasNova) recLeds.push("LED 1");
        if (hasEclipse) recLeds.push("LED 2");
        if (hasTitan && hasLunar) { recLeds.push("LED 3", "LED 4"); }
        else if (hasTitan) recLeds.push("LED 3");
        else if (hasLunar) recLeds.push("LED 4");
        else if (recLeds.length > 0) recLeds.push("LED 3");
        const applyRec = () => setForm(p => ({ ...p, selectedLEDs: recLeds.filter(l => getState(l) !== "disabled") }));

        return (
          <SectionCard
            ref={ledRef}
            icon={<TvOutlinedIcon sx={{ fontSize: 17 }} />}
            title="LED Screens Setup"
            subtitle="Enhance your event with professional screen placement"
            completed={!noHalls && ledDone}
            active={!noHalls && hallDone && !ledDone}
            badge={!noHalls && ledDone ? `${allActive.length} screens` : undefined}
          >
            {/* Hall-change alert */}
            <AnimatePresence>
              {hallChangeAlert && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                  <Alert severity="info" sx={{ mb: 2, fontSize: 12, borderRadius: "10px", border: `1px solid ${T.gold}40`, bgcolor: `${T.peach}80` }}>
                    Hall selection changed — LED setup has been reset.
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* No halls guard */}
            {noHalls ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <MeetingRoomOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, mb: 1.5, opacity: 0.4 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy, mb: 0.5 }}>Select Halls First</Typography>
                <Typography sx={{ fontSize: 12, color: T.slateLt }}>Choose your event halls in Step 1 to configure LED screens.</Typography>
              </Box>
            ) : (
            <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
              {/* LEFT: SVG Hall & LED Layout */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ bgcolor: T.bg, borderRadius: "14px", p: 2, mb: 2 }}>
                  <svg viewBox="0 0 520 250" style={{ width: "100%", height: "auto" }}>
                    <defs>
                      <filter id="glow-gold" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feFlood floodColor="#d4a373" floodOpacity="0.5" result="color" />
                        <feComposite in="color" in2="blur" operator="in" result="shadow" />
                        <feMerge><feMergeNode in="shadow" /><feMergeNode in="SourceGraphic" /></feMerge>
                      </filter>
                      <linearGradient id="stage-grad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={T.navy} stopOpacity="0.5" />
                        <stop offset="50%" stopColor={T.navy} stopOpacity="0.75" />
                        <stop offset="100%" stopColor={T.navy} stopOpacity="0.5" />
                      </linearGradient>
                    </defs>

                    <rect x="0" y="0" width="520" height="250" rx="12" fill="transparent" />

                    {/* Stage */}
                    <motion.rect
                      x="130" y="2" width="260" height="22" rx="11"
                      fill="url(#stage-grad)"
                      animate={{ opacity: [0.65, 0.8, 0.65] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <text x="260" y="17" textAnchor="middle" fill="#d4a373" fontSize="9" fontWeight="700" fontFamily="sans-serif" letterSpacing="2">STAGE</text>

                    {/* Halls — soft neutral palette */}
                    {[
                      { key: "Nova", x: 20, w: 115 },
                      { key: "Titan", x: 145, w: 115 },
                      { key: "Lunar", x: 270, w: 115 },
                      { key: "Eclipse", x: 395, w: 115 },
                    ].map(h => {
                      const sel = selectedIndividual.includes(h.key);
                      const clickable = canClickHall(h.key);
                      const dis = !clickable && !sel;
                      const cx = h.x + h.w / 2;
                      return (
                        <g key={h.key} onClick={() => handleHallClick(h.key)} style={{ cursor: clickable ? "pointer" : dis ? "not-allowed" : "default" }}>
                          {/* Subtle glow for selected */}
                          {sel && (
                            <motion.rect
                              x={h.x - 3} y="27" width={h.w + 6} height="86" rx="13"
                              fill="#d4a373" opacity={0}
                              animate={{ opacity: [0.03, 0.07, 0.03] }}
                              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                            />
                          )}
                          {/* Main hall rect */}
                          <motion.rect
                            x={h.x} y="30" width={h.w} height="80" rx="10"
                            fill={sel ? "#faf5ef" : "#fff"}
                            stroke={sel ? "#d4a373" : T.line}
                            strokeWidth={sel ? 2 : 1}
                            animate={{ y: sel ? 28 : 30, opacity: dis ? 0.35 : 1 }}
                            whileHover={clickable ? { y: 27 } : {}}
                            whileTap={clickable ? { y: 31 } : {}}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                          />
                          {/* Hall letter */}
                          <motion.text
                            x={cx} y="60" textAnchor="middle"
                            fill={sel ? T.navy : T.slateLt}
                            fontSize="13" fontWeight="800" fontFamily="sans-serif"
                            animate={{ opacity: dis ? 0.35 : 1 }}
                          >
                            {h.key.charAt(0)}
                          </motion.text>
                          {/* Hall name */}
                          <motion.text
                            x={cx} y="78" textAnchor="middle"
                            fill={sel ? T.navy : T.slate}
                            fontSize="10" fontWeight="600" fontFamily="sans-serif"
                            animate={{ opacity: dis ? 0.35 : 1 }}
                          >
                            {h.key}
                          </motion.text>
                          {/* Capacity */}
                          <motion.text
                            x={cx} y="92" textAnchor="middle"
                            fill={T.slateLt} fontSize="8" fontFamily="sans-serif"
                            animate={{ opacity: dis ? 0.25 : 0.6 }}
                          >
                            {h.key === "Titan" ? "280" : h.key === "Lunar" ? "260" : "250"} pax
                          </motion.text>
                          {/* Check dot for selected */}
                          {sel && (
                            <motion.circle
                              cx={h.x + h.w - 10} cy="40" r="5" fill="#d4a373"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 400, damping: 15 }}
                            />
                          )}
                        </g>
                      );
                    })}

                    {/* ── Connection lines — soft gold/grey ── */}
                    {/* LED 1 ← Nova (left side) */}
                    <motion.line
                      x1="30" y1="110" x2="12" y2="175"
                      stroke="#d4a373" strokeWidth="1.2" strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{
                        pathLength: allActive.includes("LED 1") ? 1 : hasNova ? 0.5 : 0,
                        opacity: allActive.includes("LED 1") ? 0.5 : hasNova ? 0.2 : 0,
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                    {/* LED 2 ← Eclipse (right side) */}
                    <motion.line
                      x1="490" y1="110" x2="508" y2="175"
                      stroke="#d4a373" strokeWidth="1.2" strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{
                        pathLength: allActive.includes("LED 2") ? 1 : hasEclipse ? 0.5 : 0,
                        opacity: allActive.includes("LED 2") ? 0.5 : hasEclipse ? 0.2 : 0,
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                    {/* LED 3 ← center-left */}
                    <motion.line
                      x1="200" y1="110" x2="180" y2="195"
                      stroke={T.slateLt} strokeWidth="1.2" strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{
                        pathLength: allActive.includes("LED 3") ? 1 : 0,
                        opacity: allActive.includes("LED 3") ? 0.4 : 0,
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                    {/* LED 4 ← center-right */}
                    <motion.line
                      x1="320" y1="110" x2="340" y2="195"
                      stroke={T.slateLt} strokeWidth="1.2" strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{
                        pathLength: allActive.includes("LED 4") ? 1 : 0,
                        opacity: allActive.includes("LED 4") ? 0.4 : 0,
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />

                    {/* ── LED Nodes ── */}
                    {[
                      { name: "LED 1", cx: 12, cy: 195 },
                      { name: "LED 2", cx: 508, cy: 195 },
                      { name: "LED 3", cx: 180, cy: 215 },
                      { name: "LED 4", cx: 340, cy: 215 },
                    ].map(node => {
                      const st = getState(node.name);
                      const isSel = allActive.includes(node.name);
                      const col = isSel ? "#d4a373" : T.slateLt;
                      return (
                        <g key={node.name} onClick={() => toggleLed(node.name)} style={{ cursor: st === "available" ? "pointer" : "default" }}>
                          {/* Pulse ring */}
                          {isSel && (
                            <motion.circle
                              cx={node.cx} cy={node.cy} r="14"
                              fill="none" stroke="#d4a373" strokeWidth="1"
                              animate={{ r: [14, 20, 14], opacity: [0.3, 0, 0.3] }}
                              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                            />
                          )}
                          {/* Main circle */}
                          <motion.circle
                            cx={node.cx} cy={node.cy} r="14"
                            fill={isSel ? "#faf5ef" : "#fff"}
                            stroke={col}
                            strokeWidth={isSel ? 2 : 1}
                            filter={isSel ? "url(#glow-gold)" : undefined}
                            animate={{
                              scale: isSel ? 1.1 : 1,
                              opacity: st === "disabled" ? 0.25 : 1,
                            }}
                            whileHover={st === "available" ? { scale: 1.15 } : {}}
                            whileTap={st === "available" ? { scale: 0.9 } : {}}
                            transition={{ type: "spring", stiffness: 300, damping: 18 }}
                            style={{ transformOrigin: `${node.cx}px ${node.cy}px` }}
                          />
                          {/* TV icon */}
                          <motion.rect
                            x={node.cx - 5} y={node.cy - 4} width="10" height="6" rx="1"
                            fill="none" stroke={col} strokeWidth="1"
                            animate={{ opacity: st === "disabled" ? 0.25 : 1 }}
                          />
                          {/* Label */}
                          <motion.text
                            x={node.cx} y={node.cy + 26} textAnchor="middle"
                            fill={isSel ? T.navy : T.slateLt} fontSize="8" fontWeight="600" fontFamily="sans-serif"
                            animate={{ opacity: st === "disabled" ? 0.25 : 1 }}
                          >
                            {node.name}
                          </motion.text>
                          {/* Price */}
                          <motion.text
                            x={node.cx} y={node.cy + 35} textAnchor="middle"
                            fill={T.slateLt} fontSize="7" fontFamily="sans-serif"
                            animate={{ opacity: st === "disabled" ? 0.15 : 0.6 }}
                          >
                            {st === "disabled" ? "" : `$${(LED_PRICING[node.name] ?? 0).toLocaleString()}`}
                          </motion.text>
                        </g>
                      );
                    })}
                  </svg>
                </Box>

                {/* Recommendation */}
                <AnimatePresence>
                  {recLeds.length > 0 && (
                    <motion.div
                      initial={{ y: 16, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 16, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 200, damping: 20 }}
                    >
                      <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#faf8f5", border: `1px solid ${T.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>Recommended</Typography>
                            <Chip label="Smart" size="small" sx={{ height: 16, fontSize: 8, bgcolor: T.navy, color: "#fff" }} />
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {recLeds.map(l => <Chip key={l} label={l} size="small" sx={{ height: 20, fontSize: 9, bgcolor: "#fff", color: T.navy, border: `1px solid ${T.line}` }} />)}
                          </Box>
                        </Box>
                        <Button size="small" variant="contained" onClick={applyRec}
                          sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, bgcolor: T.navy, borderRadius: "8px", boxShadow: "none", height: 28, "&:hover": { bgcolor: "#1a2d42" } }}>
                          Apply
                        </Button>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>

              {/* RIGHT: LED cards */}
              <Box sx={{ width: { xs: "100%", md: 240 }, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>Screens</Typography>
                {leds.map((led, idx) => {
                  const st = getState(led.name);
                  const isSel = allActive.includes(led.name);
                  const tooltipMsg = st === "disabled"
                    ? (led.name === "LED 1" ? "Available only with Nova Hall" : led.name === "LED 2" ? "Available only with Eclipse Hall" : "Not available for selected halls")
                    : "";
                  const card = (
                    <motion.div
                      key={led.name}
                      initial={{ x: 20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: idx * 0.07, type: "spring", stiffness: 200, damping: 20 }}
                      whileHover={st === "available" ? { scale: 1.02, y: -1 } : {}}
                      whileTap={st === "available" ? { scale: 0.97 } : {}}
                    >
                      <Paper elevation={0} onClick={() => toggleLed(led.name)}
                        sx={{
                          p: 1.5, mb: 1, borderRadius: "10px",
                          cursor: st === "available" ? "pointer" : st === "disabled" ? "not-allowed" : "default",
                          transition: "background 0.2s, border-color 0.2s", opacity: st === "disabled" ? 0.4 : 1,
                          border: `1.5px solid ${isSel ? "#d4a373" : "transparent"}`,
                          bgcolor: isSel ? "#faf5ef" : "#fff",
                          boxShadow: isSel ? "0 2px 12px rgba(212,163,115,0.12)" : "0 1px 3px rgba(0,0,0,0.03)",
                          "&:hover": st === "available" ? { borderColor: T.line, bgcolor: isSel ? "#faf5ef" : "#faf8f5" } : {},
                        }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: "8px",
                            bgcolor: isSel ? "rgba(212,163,115,0.12)" : T.bg,
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            transition: "all 0.2s",
                          }}>
                            <TvOutlinedIcon sx={{ fontSize: 14, color: isSel ? "#d4a373" : T.slateLt, transition: "color 0.2s" }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{led.name}</Typography>
                            <Typography sx={{ fontSize: 9, color: T.slateLt }}>{getReason(led.name)}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: isSel ? T.navy : T.slateLt, flexShrink: 0 }}>
                            {st === "disabled" ? "\u2014" : `$${led.price.toLocaleString()}`}
                          </Typography>
                        </Box>
                      </Paper>
                    </motion.div>
                  );
                  return tooltipMsg ? (
                    <Tooltip key={led.name} title={tooltipMsg} arrow placement="left">
                      <span>{card}</span>
                    </Tooltip>
                  ) : <React.Fragment key={led.name}>{card}</React.Fragment>;
                })}

                {/* Both main LEDs hint */}
                <AnimatePresence>
                  {bothMainLeds && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                      <Box sx={{ mb: 1, px: 1.5, py: 1, borderRadius: "8px", bgcolor: "#faf5ef", border: "1px solid #d4a37320", display: "flex", alignItems: "center", gap: 0.8 }}>
                        <Typography sx={{ fontSize: 9, color: T.slate, fontWeight: 600 }}>Both main stage LEDs selected</Typography>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Estimate panel */}
                <AnimatePresence>
                  {allActive.length > 0 && (
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.95, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 250, damping: 20 }}
                    >
                      <Box sx={{ mt: 1.5, bgcolor: "#faf5ef", borderRadius: "10px", p: 1.5, border: `1px solid ${T.line}` }}>
                        {allActive.map(l => (
                          <Box key={l} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.3 }}>
                            <Typography sx={{ fontSize: 9, color: T.slate, fontWeight: 500 }}>{l}</Typography>
                            <Typography sx={{ fontSize: 9, fontWeight: 600, color: T.navy }}>${(LED_PRICING[l] ?? 0).toLocaleString()}</Typography>
                          </Box>
                        ))}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 0.8, mt: 0.5, borderTop: `1px solid ${T.line}` }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.navy }}>
                            {allActive.length} screen{allActive.length !== 1 ? "s" : ""}
                          </Typography>
                          <Typography sx={{ fontSize: 18, fontWeight: 800, color: T.navy }}>${ledCost.toLocaleString()}</Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </Box>
            )}
          </SectionCard>
        );
      })()}

      {/* ══════════ FOOD SERVICE — PREMIUM UI ══════════ */}
      <SectionCard ref={foodRef} icon={<RestaurantOutlinedIcon sx={{ fontSize: 17 }} />} title="Food Service" subtitle="Catering and menu options" completed={foodDone} active={hallDone && !foodDone} badge={foodDone ? "Selected" : undefined}>

        {/* ── Food Service Dropdown (enhanced with thumbnails) ── */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.2 }}>Food Service Type</Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Food Service"
              size="small"
              fullWidth
              value={form.foodServiceOption}
              onChange={handleServiceSelect("foodServiceOption", "foodServicePricePerPerson", "food")}
              sx={{
                ...inputSx,
                "& .MuiSelect-select": { display: "flex", alignItems: "center", gap: 1.2 },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: "12px",
                      mt: 0.5,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                      border: `1px solid ${T.line}`,
                      "& .MuiMenuItem-root": {
                        borderRadius: "8px",
                        mx: 0.8,
                        my: 0.3,
                        transition: "all 0.15s ease",
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {byType("food").map((s) => {
                const dbCover = s.images?.find(i => i.is_cover) || s.images?.[0];
                const thumb = dbCover?.image_url || s.image || getFoodServiceThumb(s.name);
                const isSelected = form.foodServiceOption === s.name;
                return (
                  <MenuItem
                    key={s.id}
                    value={s.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      py: 1,
                      bgcolor: isSelected ? `${T.peach} !important` : "transparent",
                      fontWeight: isSelected ? 700 : 400,
                      "&:hover": { bgcolor: `${T.peach}80` },
                    }}
                  >
                    {thumb && (
                      <Box
                        component="img"
                        src={thumb}
                        alt=""
                        sx={{
                          width: 36,
                          height: 28,
                          borderRadius: "6px",
                          objectFit: "cover",
                          flexShrink: 0,
                          border: `1px solid ${T.line}`,
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: T.navy, lineHeight: 1.2 }}>
                        {s.name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.gold, whiteSpace: "nowrap" }}>
                      {priceLabel(s)}
                    </Typography>
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="$/person" type="number" size="small" fullWidth value={form.foodServicePricePerPerson} onChange={set("foodServicePricePerPerson")} inputProps={{ min: 0, step: "any" }} sx={inputSx} />
          </Grid>
        </Grid>

        {/* ── Food Service Image Preview + Explore ── */}
        {form.foodServiceOption ? (
          (() => {
            const sel = services.find((s) => s.serviceType === "food" && s.name === form.foodServiceOption);
            const media = resolveServiceMedia(sel, "food");
            const price = sel ? priceLabel(sel) : `$${form.foodServicePricePerPerson}/person`;
            return (
              <>
                <ServiceImagePreview
                  serviceName={form.foodServiceOption}
                  priceLabel={price}
                  onExplore={() => setFoodGalleryOpen(true)}
                  dbImages={sel?.images}
                />
                <ExploreGalleryModal
                  open={foodGalleryOpen}
                  onClose={() => setFoodGalleryOpen(false)}
                  title={form.foodServiceOption}
                  subtitle="Explore different setups and presentation styles"
                  priceLabel={price}
                  heroImage={media.coverImage}
                  items={media.gallery}
                />
              </>
            );
          })()
        ) : (
          <FoodServicePlaceholder />
        )}

        <Box sx={{ height: 1, bgcolor: T.line, my: 3 }} />

        {/* ── Menu Option Dropdown (enhanced with thumbnails) ── */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.2 }}>Menu Selection</Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Menu Option"
              size="small"
              fullWidth
              value={form.menuOption}
              onChange={handleServiceSelect("menuOption", "menuPricePerPerson", "menu")}
              sx={{
                ...inputSx,
                "& .MuiSelect-select": { display: "flex", alignItems: "center", gap: 1.2 },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: "12px",
                      mt: 0.5,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                      border: `1px solid ${T.line}`,
                      "& .MuiMenuItem-root": {
                        borderRadius: "8px",
                        mx: 0.8,
                        my: 0.3,
                        transition: "all 0.15s ease",
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {byType("menu").map((s) => {
                const thumb = getMenuThumb(s.name);
                const isSelected = form.menuOption === s.name;
                return (
                  <MenuItem
                    key={s.id}
                    value={s.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      py: 1,
                      bgcolor: isSelected ? `${T.peach} !important` : "transparent",
                      fontWeight: isSelected ? 700 : 400,
                      "&:hover": { bgcolor: `${T.peach}80` },
                    }}
                  >
                    {thumb && (
                      <Box
                        component="img"
                        src={thumb}
                        alt=""
                        sx={{
                          width: 36,
                          height: 28,
                          borderRadius: "6px",
                          objectFit: "cover",
                          flexShrink: 0,
                          border: `1px solid ${T.line}`,
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: T.navy, lineHeight: 1.2 }}>
                        {s.name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.gold, whiteSpace: "nowrap" }}>
                      {priceLabel(s)}
                    </Typography>
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="$/person" type="number" size="small" fullWidth value={form.menuPricePerPerson} onChange={set("menuPricePerPerson")} inputProps={{ min: 0, step: "any" }} sx={inputSx} />
          </Grid>
        </Grid>

        {/* ── Menu Preview Card / Explore Button ── */}
        {form.menuOption ? (
          <MenuPreviewCard
            menuName={form.menuOption}
            priceLabel={
              (() => {
                const sel = services.find((s) => s.serviceType === "menu" && s.name === form.menuOption);
                return sel ? priceLabel(sel) : `$${form.menuPricePerPerson}/person`;
              })()
            }
            onExplore={() => setMenuGalleryOpen(true)}
          />
        ) : (
          <MenuPlaceholder />
        )}

        {/* ── Menu Gallery Modal ── */}
        <MenuGalleryModal
          open={menuGalleryOpen}
          onClose={() => setMenuGalleryOpen(false)}
          menuName={form.menuOption}
          priceLabel={
            (() => {
              const sel = services.find((s) => s.serviceType === "menu" && s.name === form.menuOption);
              return sel ? priceLabel(sel) : `$${form.menuPricePerPerson}/person`;
            })()
          }
        />
      </SectionCard>

      {/* ══════════ BAR SERVICE — PREMIUM UI ══════════ */}
      <SectionCard ref={barRef} icon={<LocalBarOutlinedIcon sx={{ fontSize: 17 }} />} title="Bar Service" subtitle="Beverage packages and bartender setup" completed={barDone} active={foodDone && !barDone} badge={barDone ? "Selected" : undefined}>

        {/* ── Bar Service Dropdown (enhanced with thumbnails) ── */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.2 }}>Bar Service Type</Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Bar Service"
              size="small"
              fullWidth
              value={form.barServiceOption}
              onChange={(e) => {
                const sel = services.find((s) => s.serviceType === "bar" && s.name === e.target.value);
                setForm((prev) => ({ ...prev, barServiceOption: e.target.value, barServiceAmount: sel?.pricingType === "flat" ? String(sel.price) : "0", barServiceType: sel?.pricingType ?? "included" }));
              }}
              sx={{
                ...inputSx,
                "& .MuiSelect-select": { display: "flex", alignItems: "center", gap: 1.2 },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: "12px",
                      mt: 0.5,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                      border: `1px solid ${T.line}`,
                      "& .MuiMenuItem-root": {
                        borderRadius: "8px",
                        mx: 0.8,
                        my: 0.3,
                        transition: "all 0.15s ease",
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {byType("bar").map((s) => {
                const dbCover = s.images?.find(i => i.is_cover) || s.images?.[0];
                const thumb = dbCover?.image_url || s.image || getBarServiceThumb(s.name);
                const isSelected = form.barServiceOption === s.name;
                return (
                  <MenuItem
                    key={s.id}
                    value={s.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      py: 1,
                      bgcolor: isSelected ? `${T.peach} !important` : "transparent",
                      fontWeight: isSelected ? 700 : 400,
                      "&:hover": { bgcolor: `${T.peach}80` },
                    }}
                  >
                    {thumb && (
                      <Box
                        component="img"
                        src={thumb}
                        alt=""
                        sx={{
                          width: 36,
                          height: 28,
                          borderRadius: "6px",
                          objectFit: "cover",
                          flexShrink: 0,
                          border: `1px solid ${T.line}`,
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: T.navy, lineHeight: 1.2 }}>
                        {s.name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.gold, whiteSpace: "nowrap" }}>
                      {priceLabel(s)}
                    </Typography>
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Bar Amount ($)" type="number" size="small" fullWidth value={form.barServiceAmount} onChange={set("barServiceAmount")} inputProps={{ min: 0, step: "any" }} sx={inputSx} />
          </Grid>
        </Grid>

        {/* ── Bar Service Image Preview + Explore ── */}
        {form.barServiceOption ? (
          (() => {
            const sel = services.find((s) => s.serviceType === "bar" && s.name === form.barServiceOption);
            const media = resolveServiceMedia(sel, "bar");
            const price = sel ? priceLabel(sel) : `$${form.barServiceAmount} flat`;
            return (
              <>
                <ServiceImagePreview
                  type="bar"
                  serviceName={form.barServiceOption}
                  priceLabel={price}
                  onExplore={() => setBarGalleryOpen(true)}
                  dbImages={sel?.images}
                />
                <ExploreGalleryModal
                  open={barGalleryOpen}
                  onClose={() => setBarGalleryOpen(false)}
                  title={form.barServiceOption}
                  subtitle="Explore bar setups and drink experiences"
                  priceLabel={price}
                  heroImage={media.coverImage}
                  items={media.gallery}
                />
              </>
            );
          })()
        ) : (
          <BarServicePlaceholder />
        )}

        <Box sx={{ height: 1, bgcolor: T.line, my: 3 }} />

        {/* ── Bar Details ── */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.2 }}>Bar Details</Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={6} sm={3}>
            <TextField label="Bar Patrons" type="number" size="small" fullWidth value={form.barPatrons} onChange={set("barPatrons")} inputProps={{ min: 0 }} sx={inputSx} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Bartenders" type="number" size="small" fullWidth value={form.bartenderCount} onChange={set("bartenderCount")} inputProps={{ min: 0 }} sx={inputSx} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Bartender Rate ($)" type="number" size="small" fullWidth value={form.bartenderAmount} onChange={set("bartenderAmount")} inputProps={{ min: 0, step: "any" }} sx={inputSx} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="Bar Hours" type="number" size="small" fullWidth value={form.barHours} onChange={set("barHours")} inputProps={{ min: 0 }} sx={inputSx} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="LCBO Licence #" size="small" fullWidth value={form.lcboLicence} onChange={set("lcboLicence")} sx={inputSx} />
          </Grid>
        </Grid>

        <Box sx={{ height: 1, bgcolor: T.line, my: 3 }} />

        {/* ── Bar Package (enhanced dropdown with thumbnails) ── */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.2 }}>Bar Package</Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Bar Package"
              size="small"
              fullWidth
              value={form.barPackageOption}
              onChange={handleBarPackageSelect}
              sx={{
                ...inputSx,
                "& .MuiSelect-select": { display: "flex", alignItems: "center", gap: 1.2 },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: "12px",
                      mt: 0.5,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                      border: `1px solid ${T.line}`,
                      "& .MuiMenuItem-root": {
                        borderRadius: "8px",
                        mx: 0.8,
                        my: 0.3,
                        transition: "all 0.15s ease",
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {["Standard", "Premium", "Deluxe", "Other"].map((cat) => {
                const items = byType("package").filter((s) => s.category === cat);
                if (!items.length) return null;
                return [
                  <MenuItem key={`h-${cat}`} disabled sx={{ fontSize: 11, fontWeight: 700, opacity: 1, color: T.slateLt }}>— {cat} —</MenuItem>,
                  ...items.map((s) => (
                    <MenuItem
                      key={s.id}
                      value={s.name}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        py: 1,
                        bgcolor: form.barPackageOption === s.name ? `${T.peach} !important` : "transparent",
                        fontWeight: form.barPackageOption === s.name ? 700 : 400,
                        "&:hover": { bgcolor: `${T.peach}80` },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: form.barPackageOption === s.name ? 700 : 500, color: T.navy, lineHeight: 1.2 }}>
                          {s.name}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.gold, whiteSpace: "nowrap" }}>
                        {priceLabel(s)}
                      </Typography>
                    </MenuItem>
                  )),
                ];
              })}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField label="$/person" type="number" size="small" fullWidth value={form.barPackagePricePerPerson} onChange={set("barPackagePricePerPerson")} inputProps={{ min: 0, step: "any" }} sx={inputSx} />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Financial Summary, Security Deposit, and Payments moved to Step 3 */}

      {/* Adjacent-hall toast */}
      <Snackbar open={adjToast} autoHideDuration={3000} onClose={() => setAdjToast(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setAdjToast(false)} severity="warning" variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13 }}>
          Please select adjacent halls only
        </Alert>
      </Snackbar>

      {/* LED alert toast */}
      <Snackbar open={!!ledToast} autoHideDuration={3500} onClose={() => setLedToast("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setLedToast("")} severity="warning" variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13 }}>
          {ledToast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Step2Services;
